/**
 * Reconnaissance d'un **verbe d'instruction** dans une ligne de recette.
 *
 * Écrit pour US-03 puis étendu par US-26, ce test vivait à l'intérieur de
 * `server/src/import/splitPastedRecipeText.ts`, où il servait à décider si une
 * ligne était une étape ou une note. **Remonté ici en US-76**, où il prend un
 * second rôle, plus exigeant : celui d'**arbitre du regroupement des étapes**.
 *
 * ## Pourquoi la remontée n'est pas un simple déplacement
 *
 * Dans son premier rôle, un verbe manquant coûte peu : la ligne tombe en note,
 * et rien du texte n'est perdu (§3.2, « le doute profite au texte source »).
 * Dans le second, il coûte cher. `estUnTitreDeSection` accepte toute ligne
 * courte, sans chiffre, qui commence par « pour » ou se termine par deux
 * points. Sans verbe reconnu, **« Pour finir, dressez sur assiette. » devient
 * un titre de section, et l'instruction disparaît** — exactement ce qu'US-41
 * s'interdisait côté ingrédients (« les deviner reviendrait à effacer un
 * ingrédient »), en plus coûteux : une étape manquante ne se remarque qu'en
 * cuisine, le plat commencé.
 *
 * ## Une conjugaison sur deux manquait
 *
 * La liste d'origine énumérait les formes à la main, et l'énumération était
 * incomplète : `cuire` sans `cuisez`, `préparer` sans `préparez`, `dresser`
 * sans `dressez` — vingt-deux formes de 2ᵉ personne du pluriel absentes sur
 * une liste qui en portait autant. Invisible tant que le manque n'envoyait
 * qu'une ligne en note ; fautif dès qu'il efface une étape.
 *
 * La liste est donc **dérivée** et non plus énumérée : un radical de verbe du
 * premier groupe donne ses deux formes (« mélanger », « mélangez »), et les
 * irréguliers sont les seuls écrits en toutes lettres, **par paires**. Le trou
 * ne peut plus se rouvrir par distraction. Aucun verbe nouveau n'est entré à
 * cette occasion : la liste couvre exactement les mêmes verbes qu'avant, dans
 * leurs deux formes.
 *
 * Elle reste **volontairement large et non exhaustive** : un verbe absent
 * renvoie la ligne en note (premier rôle) ou laisse un titre passer pour une
 * étape (second rôle). Les deux se corrigent d'un geste à l'écran d'édition ;
 * une étape effacée, non.
 */

const DIACRITIQUES = new RegExp('[\\u0300-\\u036f]', 'g');

/** Accents retirés : la liste s'écrit sans eux, le texte est replié pour la rejoindre. */
function replierLesAccents(texte: string): string {
  return texte.normalize('NFD').replace(DIACRITIQUES, '');
}

/**
 * Radicaux de verbes du premier groupe. Chacun donne l'infinitif et
 * l'impératif de 2ᵉ personne du pluriel — les deux formes d'une recette
 * française écrite, « mélanger le tout » et « mélangez le tout ».
 */
const RADICAUX_EN_ER = [
  'prechauff', 'melang', 'ajout', 'vers', 'incorpor', 'laiss', 'coup', 'epluch',
  'reserv', 'enfourn', 'retir', 'dispos', 'chauff', 'sal', 'poivr', 'assaisonn',
  'mont', 'fouett', 'etal', 'beurr', 'graiss', 'prepar', 'depos', 'arros',
  'egoutt', 'rinc', 'lav', 'hach', 'eminc', 'rap', 'press', 'filtr', 'napp',
  'decor', 'dress', 'demoul',
];

/**
 * Verbes irréguliers, par paires explicites — infinitif puis 2ᵉ personne du
 * pluriel. Ce sont les seuls que la dérivation ne sait pas produire.
 */
const IRREGULIERS = [
  'cuire', 'cuisez',
  'battre', 'battez',
  'faire', 'faites',
  'servir', 'servez',
  'recouvrir', 'recouvrez',
  'garnir', 'garnissez',
  'attendre', 'attendez',
  'sortir', 'sortez',
];

/** Toutes les formes reconnues, les plus longues d'abord (l'alternance d'une expression régulière est gloutonne à gauche). */
const FORMES = [
  ...RADICAUX_EN_ER.flatMap((radical) => [`${radical}er`, `${radical}ez`]),
  ...IRREGULIERS,
].sort((a, b) => b.length - a.length);

const MOTIF = new RegExp(`\\b(?:${FORMES.join('|')})\\b`, 'i');

/**
 * Cette ligne porte-t-elle un verbe d'instruction ?
 *
 * Deux appelants, deux enjeux : `splitPastedRecipeText` s'en sert pour classer
 * une ligne en étape plutôt qu'en note ; `buildSteps` s'en sert pour refuser
 * qu'une instruction soit prise pour un titre de section.
 */
export function contientUnVerbeDEtape(ligne: string): boolean {
  return MOTIF.test(replierLesAccents(ligne));
}

/** Les formes reconnues, exposées pour que la suite de tests puisse épingler les paires. */
export const FORMES_DE_VERBE_DETAPE: readonly string[] = FORMES;
